/**
 * <HighsLows> — 52주 신고가 / 신저가
 *
 * 모멘텀 트레이더 필수. 신고가 = 추세 진행 / 신저가 = 추세 반전 가능.
 * 데이터: GET /api/ticker/market/highs-lows → { highs: [...], lows: [...] }
 */
"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import type { WidgetMeta, WidgetProps } from "../types";
import { koreanCompanyName } from "@/lib/korean-company-name";

interface Item {
  symbol: string;
  name?: string | null;
  price: number | null;
  high52w: number | null;
  low52w: number | null;
  changePercent: number | null;
}

interface HighsLowsData {
  highs: Item[];
  lows: Item[];
}

const REFRESH_MS = 5 * 60 * 1000;

function fmtPrice(v: number | null): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toFixed(2);
}

function fmtPct(v: number | null): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const sign = v >= 0 ? "+" : "";
  return `${sign}${(v * 100).toFixed(2)}%`;
}

function ItemList({ items, kind }: { items: Item[]; kind: "high" | "low" }) {
  if (items.length === 0) return <div className="dw-hl-empty">데이터 없음</div>;
  return (
    <div className="dw-hl-list">
      {items.slice(0, 5).map((it) => {
        const pct = it.changePercent;
        const ref = kind === "high" ? it.high52w : it.low52w;
        return (
          <Link
            key={it.symbol}
            href={`/analyze/${encodeURIComponent(it.symbol)}`}
            className="dw-hl-row"
            title={`${it.symbol} · ${koreanCompanyName(it.symbol, it.name)}`}
          >
            <span className="dw-hl-name-block">
              <span className="dw-hl-sym">{koreanCompanyName(it.symbol, it.name)}</span>
              <span className="dw-hl-ticker">{it.symbol}</span>
            </span>
            <span className="dw-hl-price">{fmtPrice(it.price)}</span>
            <span className="dw-hl-ref">{kind === "high" ? "고" : "저"} {fmtPrice(ref)}</span>
            <span className={`dw-hl-pct ${kind === "high" ? "up" : "down"}`}>{fmtPct(pct)}</span>
          </Link>
        );
      })}
    </div>
  );
}

export function HighsLows(_props: WidgetProps) {
  const [data, setData] = useState<HighsLowsData | null>(null);
  const [tab, setTab] = useState<"highs" | "lows">("highs");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    async function load() {
      try {
        const res = await fetch("/api/ticker/market/highs-lows", { credentials: "include" });
        if (!res.ok) {
          if (mountedRef.current) { setError("로드 실패"); setLoading(false); }
          return;
        }
        const d = await res.json();
        if (mountedRef.current) {
          setData(d);
          setError(null);
          setLoading(false);
        }
      } catch {
        if (mountedRef.current) { setError("네트워크 오류"); setLoading(false); }
      }
    }
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => { mountedRef.current = false; clearInterval(t); };
  }, []);

  return (
    <div className="dw-body dw-hl">
      <div className="dw-head"><span className="dw-title">52주 신고가·신저가</span></div>
      <div className="dw-hl-tabs">
        <button
          type="button"
          className={`dw-hl-tab ${tab === "highs" ? "is-active up" : ""}`}
          onClick={() => setTab("highs")}
        >▲ 신고가</button>
        <button
          type="button"
          className={`dw-hl-tab ${tab === "lows" ? "is-active down" : ""}`}
          onClick={() => setTab("lows")}
        >▼ 신저가</button>
      </div>
      {error ? (
        <div className="dw-hl-error">{error}</div>
      ) : loading && !data ? (
        <div className="dw-hl-empty">로딩 중…</div>
      ) : tab === "highs" ? (
        <ItemList items={data?.highs || []} kind="high" />
      ) : (
        <ItemList items={data?.lows || []} kind="low" />
      )}
    </div>
  );
}

export const highsLowsMeta: WidgetMeta = {
  type: "highs-lows",
  name: "52주 신고가·신저가",
  description: "모멘텀 시그널 · 신고가 추세 / 신저가 반전",
  icon: "🎯",
  category: "discovery",
  defaultSize: { w: 4, h: 5 },
  availableSizes: [
    { w: 3, h: 5 },
    { w: 4, h: 5 },
    { w: 4, h: 6 },
  ],
  minW: 3,
  maxW: 6,
  minH: 4,
  maxH: 7,
  component: HighsLows,
};
